import { useState } from 'react';
import { useUIStore } from '@/state/store';
import { useChatStore } from '@/state/chatStore';
import { apiFetch } from '@/lib/auth';
import { saveCallSession } from '@/lib/callSession';
import { useRoster } from '@/hooks/useRoster';
import { useRingingCalls } from '@/hooks/useRingingCalls';

const STATUS_COLOR: Record<string, string> = {
  available: '#22C55E', pause: '#F59E0B', wrap: '#3B82F6', incall: '#EF4444', phone: '#EF4444', offline: '#9AA0A6',
};
const STATUS_LABEL: Record<string, string> = {
  available: 'verfügbar', pause: 'Pause', wrap: 'Nachbereitung', incall: 'im Call', phone: 'Besetzt', offline: 'offline',
};
const ALL_ID = '__all__';

export function Roster({ remote = false }: { remote?: boolean } = {}) {
  const { roster, reload } = useRoster();
  const { calls } = useRingingCalls();
  const setView = useUIStore((s) => s.setView);

  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});  // per Default ALLES eingeklappt
  const [pickupBusy, setPickupBusy] = useState<string | null>(null);
  const [joinBusy, setJoinBusy] = useState<string | null>(null);
  const [err, setErr] = useState('');

  const q = query.trim().toLowerCase();
  const match = (name: string) => q === '' || name.toLowerCase().includes(q);
  const toggle = (id: string) => setExpanded((c) => ({ ...c, [id]: !c[id] }));
  const chatScopes = useChatStore((s) => s.unread.scopes);

  const myGroupIds = new Set(roster.groups.map((g) => g.id));
  const ringingFor = (gid: string) => calls.filter((c) => c.groupId === gid && myGroupIds.has(c.groupId));

  async function toggleJoin(gid: string, current: boolean) {
    setJoinBusy(gid); setErr('');
    try {
      await apiFetch(`/api/my-groups/${gid}/active`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ active: !current }),
      });
    } catch {
      setErr('An-/Abmeldung fehlgeschlagen.');
    } finally {
      setJoinBusy(null);
      await reload();
    }
  }

  async function pickup(callId: string, steleName: string, groupName: string) {
    setPickupBusy(callId); setErr('');
    try {
      const r = await apiFetch(`/api/calls/${callId}/pickup`, { method: 'POST' });
      if (r.ok) {
        const data = await r.json();
        const incoming = { callId: data.callId, steleName, tenantName: '', group: groupName, strategy: 'pickup', waitingMs: 0 };
        (useUIStore.getState() as any).rtcToken = data.rtcToken;
        (useUIStore.getState() as any).activeCallId = data.callId;
        (window as any).__rtcToken = data.rtcToken;
        (window as any).__activeCallId = data.callId;
        (window as any).__incomingCall = incoming;
        saveCallSession({ callId: data.callId, rtcToken: data.rtcToken, incoming });
        setView('incall');
      } else {
        const b = await r.json().catch(() => ({}));
        setErr(b?.message || 'Pickup nicht möglich.');
      }
    } catch {
      setErr('Verbindung fehlgeschlagen.');
    } finally {
      setPickupBusy(null);
    }
  }

  const dot = (color: string) => <span style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: color }} />;

  // Aufkommen-Ampel: rot = niemand angemeldet, gelb = Calls warten, grün = ok
  const loadColor = (activeCount: number, waiting: number) => (activeCount === 0 ? '#EF4444' : waiting > 0 ? '#F59E0B' : '#22C55E');

  return (
    <div className="sidebar-section">
      <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Berater suchen…" style={searchInput} />
      {err && <div style={{ color: '#d8785a', fontSize: 11.5, margin: '4px 2px' }}>{err}</div>}

      {/* Eigene Gruppen – per Default eingeklappt, Kopfzeile zeigt alles Wichtige */}
      {roster.groups.map((g) => {
        const ring = ringingFor(g.id);
        const members = g.members.filter((m) => match(m.displayName));
        if (q !== '' && members.length === 0 && ring.length === 0) return null;
        const memberCount = g.memberCount ?? g.members.length;
        const activeCount = g.activeCount ?? g.members.filter((m) => m.active).length;
        const waiting = g.waiting ?? ring.length;
        const open = expanded[g.id] || q !== '';
        const noneActive = activeCount === 0;
        const groupUnread = g.members.reduce((sum, m) => sum + (chatScopes[m.id] || 0), 0);
        return (
          <div key={g.id} style={{ marginBottom: 6 }}>
            <div style={groupHead}>
              <button onClick={() => toggle(g.id)} style={groupHeadBtn} title={`${waiting} wartend · ${activeCount}/${memberCount} angemeldet`}>
                <span style={{ transform: open ? 'none' : 'rotate(-90deg)', transition: 'transform .15s', fontSize: 10 }}>▾</span>
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{g.name}</span>
                {noneActive && <span title="Niemand angemeldet – Calls bleiben unbeantwortet" style={{ color: '#EF4444', fontSize: 12 }}>⚠</span>}
              </button>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                {groupUnread > 0 && <span title="ungelesene Nachrichten" style={{ minWidth: 16, height: 16, padding: '0 4px', borderRadius: 8, background: '#EF4444', color: '#fff', fontSize: 10, fontWeight: 700, lineHeight: '16px', textAlign: 'center', boxSizing: 'border-box' }}>{groupUnread > 9 ? '9+' : groupUnread}</span>}
                <span title={`${waiting} wartend`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  {dot(loadColor(activeCount, waiting))}
                  {waiting > 0 && <span style={{ fontSize: 10.5, color: '#F59E0B', fontWeight: 700 }}>{waiting}</span>}
                </span>
                <span style={{ fontSize: 11, fontWeight: 700, color: noneActive ? '#EF4444' : 'var(--muted, #8aa)' }} title="angemeldet / Mitglieder">{activeCount}/{memberCount}</span>
                <button
                  onClick={() => toggleJoin(g.id, !!g.myActive)} disabled={joinBusy === g.id}
                  title={g.myActive ? 'In dieser Gruppe angemeldet – tippen zum Abmelden' : 'Nicht angemeldet – tippen zum Anmelden'}
                  style={{ ...joinSwitch, background: g.myActive ? 'var(--tenant-accent, #7c3aed)' : 'var(--bg-elev-2)', opacity: joinBusy === g.id ? 0.5 : 1 }}>
                  <span style={{ ...joinKnob, transform: g.myActive ? 'translateX(12px)' : 'translateX(1px)' }} />
                </button>
              </span>
            </div>

            {open && (
              <div style={{ paddingLeft: 4 }}>
                {ring.map((c) => (
                  <div key={c.callId} style={ringRow}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
                      <span style={ringPulse} />
                      <span style={{ fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.steleName}</span>
                    </span>
                    {remote ? (
                      <span style={{ fontSize: 10.5, color: 'var(--warn, #F59E0B)', fontWeight: 600 }}>klingelt</span>
                    ) : c.pickupEnabled && g.pickupEnabled ? (
                      <button onClick={() => pickup(c.callId, c.steleName, g.name)} disabled={pickupBusy === c.callId} style={pickupBtn}>
                        {pickupBusy === c.callId ? '…' : 'Pickup'}
                      </button>
                    ) : (
                      <span style={{ fontSize: 10.5, color: 'var(--muted, #8aa)' }}>kein Pickup</span>
                    )}
                  </div>
                ))}
                {members.map((m) => (
                  <div key={m.id} style={memberRow}>
                    <span title={STATUS_LABEL[m.status] || m.status}>{dot(STATUS_COLOR[m.status] || STATUS_COLOR.offline)}</span>
                    <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', opacity: m.active ? 1 : 0.55 }}>{m.displayName}</span>
                    <span style={{ flexShrink: 0, fontSize: 10, fontWeight: 600, letterSpacing: '.02em', color: STATUS_COLOR[m.status] || STATUS_COLOR.offline }}>{STATUS_LABEL[m.status] || 'offline'}</span>
                    <ChatIconSlot peerId={m.id} title={m.displayName} />
                  </div>
                ))}
                {members.length === 0 && ring.length === 0 && (
                  <div style={{ fontSize: 11.5, color: 'var(--muted, #8aa)', padding: '4px 8px' }}>keine Berater</div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Alle Berater – per Default eingeklappt, Status nur bei gemeinsamen Gruppen */}
      {(() => {
        const all = roster.all.filter((a) => match(a.displayName));
        if (q !== '' && all.length === 0) return null;
        const open = expanded[ALL_ID] || q !== '';
        return (
          <div style={{ marginBottom: 6 }}>
            <button onClick={() => toggle(ALL_ID)} style={{ ...groupHeadBtn, width: '100%' }}>
              <span style={{ transform: open ? 'none' : 'rotate(-90deg)', transition: 'transform .15s', fontSize: 10 }}>▾</span>
              Alle
              <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--muted, #8aa)' }}>{roster.all.length}</span>
            </button>
            {open && (
              <div style={{ paddingLeft: 4 }}>
                {all.map((a) => (
                  <div key={a.id} style={{ ...memberRow, opacity: a.sharedGroup ? 1 : 0.7 }}
                    title={a.sharedGroup ? (STATUS_LABEL[a.status || 'offline'] || '') : 'Kein gemeinsamer Gruppenkontext'}>
                    {a.sharedGroup
                      ? <span>{dot(STATUS_COLOR[a.status || 'offline'] || STATUS_COLOR.offline)}</span>
                      : <span style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, border: '1px solid var(--hairline, #333)' }} />}
                    <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.displayName}</span>
                    {a.sharedGroup && <span style={{ flexShrink: 0, fontSize: 10, fontWeight: 600, letterSpacing: '.02em', color: STATUS_COLOR[a.status || 'offline'] || STATUS_COLOR.offline }}>{STATUS_LABEL[a.status || 'offline'] || 'offline'}</span>}
                    {a.sharedGroup && <ChatIconSlot peerId={a.id} title={a.displayName} />}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

/** Berater-Chat-Einstieg (pre186): öffnet eine 1:1 Ende-zu-Ende-Unterhaltung; zeigt Ungelesen-Punkt. */
function ChatIconSlot({ peerId, title }: { peerId: string; title: string }) {
  const openChat = useChatStore((s) => s.openChat);
  const n = useChatStore((s) => s.unread.scopes[peerId] || 0);
  return (
    <button
      title={`Direktnachricht (E2E) – ${title}`}
      onClick={(e) => { e.stopPropagation(); openChat(peerId, title); }}
      style={{ ...chatIcon, position: 'relative', opacity: n > 0 ? 1 : 0.6 }} aria-label="Chat">
      <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
      </svg>
      {n > 0 && <span style={{ position: 'absolute', top: 0, right: 0, width: 7, height: 7, borderRadius: '50%', background: 'var(--tenant-accent, #7c3aed)' }} />}
    </button>
  );
}

const searchInput: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', padding: '7px 10px', marginBottom: 8,
  borderRadius: 8, border: '1px solid var(--hairline, #232323)', background: 'var(--bg-elev-2)',
  color: 'var(--fg, #eee)', fontSize: 12.5, fontFamily: 'inherit', outline: 'none',
};
const groupHead: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, padding: '5px 8px',
};
const groupHeadBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0, flex: 1,
  background: 'transparent', border: 'none', cursor: 'pointer',
  color: 'var(--fg, #eee)', fontSize: 12.5, fontWeight: 700, fontFamily: 'inherit', textAlign: 'left', padding: 0,
};
const joinSwitch: React.CSSProperties = {
  position: 'relative', width: 26, height: 15, borderRadius: 999, border: '1px solid var(--hairline, #333)',
  cursor: 'pointer', flexShrink: 0, padding: 0, transition: 'background .15s',
  WebkitBackdropFilter: 'blur(10px)', backdropFilter: 'blur(10px)',
  boxShadow: 'inset 0 0 0 1px #ffffff14, 0 1px 3px #00000066',
};
const joinKnob: React.CSSProperties = {
  position: 'absolute', top: 1, left: 0, width: 11, height: 11, borderRadius: '50%', background: '#fff', transition: 'transform .15s',
  boxShadow: '0 1px 2px #0007',
};
const memberRow: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px' };
const chatIcon: React.CSSProperties = {
  flexShrink: 0, background: 'transparent', border: 'none', cursor: 'pointer', padding: 2,
  color: 'var(--muted, #8aa)', opacity: 0.6, display: 'inline-flex', alignItems: 'center',
};
const ringRow: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
  padding: '6px 8px', margin: '2px 0', borderRadius: 8,
  background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.30)',
};
const ringPulse: React.CSSProperties = { width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: '#EF4444' };
const pickupBtn: React.CSSProperties = {
  border: 'none', borderRadius: 7, padding: '4px 12px', cursor: 'pointer',
  background: '#EF4444', color: '#fff', fontSize: 11.5, fontWeight: 700, fontFamily: 'inherit', flexShrink: 0,
};
